import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

const ImageEditor = ({
  image,
  onSave,
  onCancel,
  aspectRatio = undefined,
  originalAspectRatio = null,
  showFilters = true,
  showAdjustments = true,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentAspectRatio, setCurrentAspectRatio] = useState(() => {
    // If we have an original aspect ratio, use it
    if (originalAspectRatio !== null && originalAspectRatio !== undefined) {
      return originalAspectRatio;
    }
    // If we have a specific aspect ratio prop, use it
    if (aspectRatio !== undefined) {
      return aspectRatio;
    }
    // Otherwise, default to "Free" (undefined)
    return undefined;
  });
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    blur: 0,
    sepia: 0,
  });
  const [activeFilter, setActiveFilter] = useState('normal');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showControls, setShowControls] = useState(false);

  // Predefined aspect ratio options
  const aspectRatioOptions = [
    ...(originalAspectRatio && originalAspectRatio !== aspectRatio
      ? [{ label: `Original (${originalAspectRatio.toFixed(2)}:1)`, value: originalAspectRatio }]
      : []),
    { label: 'Square (1:1)', value: 1 },
    { label: 'Portrait (3:4)', value: 3 / 4 },
    { label: 'Landscape (4:3)', value: 4 / 3 },
    { label: 'Wide (16:9)', value: 16 / 9 },
    { label: 'Ultra Wide (21:9)', value: 21 / 9 },
    { label: 'Free (No constraint)', value: undefined },
  ];

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Initialize crop area to match image size when image loads
  const onMediaLoaded = useCallback(mediaSize => {
    // The react-easy-crop library automatically centers the crop area to match the full image
    // We don't need to do anything special here as the default behavior is what we want
    console.log('Image loaded with dimensions:', mediaSize);
  }, []);

  const handleAspectRatioChange = newAspectRatio => {
    setCurrentAspectRatio(newAspectRatio);
    // Reset crop position when changing aspect ratio
    setCrop({ x: 0, y: 0 });
  };

  const getFilterStyle = () => {
    const { brightness, contrast, saturation, blur, sepia } = filters;
    return {
      filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px) sepia(${sepia}%)`,
    };
  };

  const applyPresetFilter = filterName => {
    setActiveFilter(filterName);
    switch (filterName) {
      case 'normal':
        setFilters({ brightness: 100, contrast: 100, saturation: 100, blur: 0, sepia: 0 });
        break;
      case 'vintage':
        setFilters({ brightness: 110, contrast: 120, saturation: 80, blur: 0, sepia: 30 });
        break;
      case 'blackAndWhite':
        setFilters({ brightness: 100, contrast: 120, saturation: 0, blur: 0, sepia: 0 });
        break;
      case 'warm':
        setFilters({ brightness: 105, contrast: 110, saturation: 120, blur: 0, sepia: 10 });
        break;
      case 'cool':
        setFilters({ brightness: 95, contrast: 110, saturation: 80, blur: 0, sepia: 0 });
        break;
      case 'dramatic':
        setFilters({ brightness: 90, contrast: 130, saturation: 90, blur: 0, sepia: 0 });
        break;
      default:
        break;
    }
  };

  const createImage = url => {
    return new Promise((resolve, reject) => {
      const image = new window.Image();

      // Only set crossOrigin for HTTP/HTTPS URLs, not for blob URLs
      if (url.startsWith('http://') || url.startsWith('https://')) {
        image.crossOrigin = 'anonymous';
      }

      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });
  };

  const getCroppedImg = async (imageSrc, crop, rotation = 0) => {
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const maxSize = Math.max(image.width, image.height);
      const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

      canvas.width = safeArea;
      canvas.height = safeArea;

      ctx.translate(safeArea / 2, safeArea / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-safeArea / 2, -safeArea / 2);

      ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);

      // Try to get image data, handle CORS errors gracefully
      let data;
      try {
        data = ctx.getImageData(0, 0, safeArea, safeArea);
      } catch (corsError) {
        console.warn('CORS error when getting image data, using alternative method:', corsError);
        // If CORS fails, create a new canvas and draw the image again
        const newCanvas = document.createElement('canvas');
        const newCtx = newCanvas.getContext('2d');
        newCanvas.width = safeArea;
        newCanvas.height = safeArea;

        newCtx.translate(safeArea / 2, safeArea / 2);
        newCtx.rotate((rotation * Math.PI) / 180);
        newCtx.translate(-safeArea / 2, -safeArea / 2);
        newCtx.drawImage(
          image,
          safeArea / 2 - image.width * 0.5,
          safeArea / 2 - image.height * 0.5
        );

        // Return the canvas directly instead of trying to manipulate pixel data
        return newCanvas;
      }

      canvas.width = crop.width;
      canvas.height = crop.height;

      ctx.putImageData(
        data,
        0 - safeArea / 2 + image.width * 0.5 - crop.x,
        0 - safeArea / 2 + image.height * 0.5 - crop.y
      );

      return canvas;
    } catch (error) {
      console.error('Error in getCroppedImg:', error);
      throw error;
    }
  };

  const applyFiltersToCanvas = canvas => {
    const ctx = canvas.getContext('2d');

    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      const { brightness, contrast } = filters;

      // Apply brightness and contrast
      const brightnessFactor = brightness / 100;
      const contrastFactor = contrast / 100;

      for (let i = 0; i < data.length; i += 4) {
        // Brightness
        data[i] = data[i] * brightnessFactor; // Red
        data[i + 1] = data[i + 1] * brightnessFactor; // Green
        data[i + 2] = data[i + 2] * brightnessFactor; // Blue

        // Contrast
        data[i] = (data[i] - 128) * contrastFactor + 128;
        data[i + 1] = (data[i + 1] - 128) * contrastFactor + 128;
        data[i + 2] = (data[i + 2] - 128) * contrastFactor + 128;

        // Clamp values
        data[i] = Math.max(0, Math.min(255, data[i]));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1]));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2]));
      }

      ctx.putImageData(imageData, 0, 0);
    } catch (corsError) {
      console.warn('CORS error when applying filters, skipping filter application:', corsError);
      // If we can't manipulate pixel data due to CORS, just return without applying filters
      // The image will still be cropped and rotated, just without the filter effects
    }
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const canvas = await getCroppedImg(image, croppedAreaPixels, rotation);
      applyFiltersToCanvas(canvas);

      canvas.toBlob(
        blob => {
          if (blob) {
            const file = new File([blob], 'edited-image.jpg', { type: 'image/jpeg' });
            onSave(file);
          } else {
            console.error('Failed to create blob from canvas');
            alert('Failed to process image. Please try again.');
          }
          setIsProcessing(false);
        },
        'image/jpeg',
        0.9
      );
    } catch (error) {
      console.error('Error processing image:', error);
      alert(
        'Error processing image. This may be due to browser security restrictions. Please try with a different image or browser.'
      );
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    // Reset to the initial aspect ratio logic
    if (originalAspectRatio !== null && originalAspectRatio !== undefined) {
      setCurrentAspectRatio(originalAspectRatio);
    } else if (aspectRatio !== undefined) {
      setCurrentAspectRatio(aspectRatio);
    } else {
      setCurrentAspectRatio(undefined);
    }
    setFilters({ brightness: 100, contrast: 100, saturation: 100, blur: 0, sepia: 0 });
    setActiveFilter('normal');
    // Reset crop area to full image - this will be handled by initialCropAreaPercentages
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-2 sm:p-4">
      <div className="bg-white rounded-xl w-full h-full sm:max-w-6xl sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit Image</h3>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={handleReset}
              className="px-2 py-1 sm:px-3 text-xs sm:text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            >
              Reset
            </button>
            <button
              onClick={onCancel}
              className="px-2 py-1 sm:px-3 text-xs sm:text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isProcessing}
              className="px-3 py-1 sm:px-4 text-xs sm:text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? 'Processing...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Mobile Controls Toggle */}
        <div className="sm:hidden border-b border-gray-200">
          <button
            onClick={() => setShowControls(!showControls)}
            className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
          >
            <span>Edit Controls</span>
            <span className={`transform transition-transform ${showControls ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
        </div>

        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
          {/* Main Editor Area */}
          <div className="flex-1 relative bg-gray-900 min-h-0">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-full h-full max-w-2xl max-h-96">
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={currentAspectRatio}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                  showGrid={false}
                  initialCropAreaPercentages={{
                    x: 0,
                    y: 0,
                    width: 100,
                    height: 100,
                  }}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000',
                    },
                    mediaStyle: {
                      ...getFilterStyle(),
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Controls Panel */}
          <div
            className={`bg-gray-50 border-t sm:border-t-0 sm:border-l border-gray-200 overflow-y-auto transition-all duration-300 ${
              showControls ? 'max-h-96 sm:max-h-none' : 'max-h-0 sm:max-h-none'
            } sm:w-80 sm:flex-shrink-0`}
          >
            <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
              {/* Crop Controls */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Crop & Rotate</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Zoom</label>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.1"
                      value={zoom}
                      onChange={e => setZoom(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{Math.round(zoom * 100)}%</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Rotation</label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setRotation(rotation - 90)}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded touch-manipulation"
                      >
                        ↺
                      </button>
                      <span className="text-xs text-gray-500 flex-1 text-center">{rotation}°</span>
                      <button
                        onClick={() => setRotation(rotation + 90)}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded touch-manipulation"
                      >
                        ↻
                      </button>
                    </div>
                  </div>

                  {/* Aspect Ratio Selector */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Aspect Ratio</label>
                    <select
                      value={currentAspectRatio || 'free'}
                      onChange={e =>
                        handleAspectRatioChange(
                          e.target.value === 'free' ? undefined : Number(e.target.value)
                        )
                      }
                      className="w-full text-xs border border-gray-300 rounded px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {aspectRatioOptions.map(option => (
                        <option key={option.value || 'free'} value={option.value || 'free'}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Filters */}
              {showFilters && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Filters</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {['normal', 'vintage', 'blackAndWhite', 'warm', 'cool', 'dramatic'].map(
                      filter => (
                        <button
                          key={filter}
                          onClick={() => applyPresetFilter(filter)}
                          className={`p-2 text-xs rounded border transition-colors touch-manipulation ${
                            activeFilter === filter
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {filter
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/^./, str => str.toUpperCase())}
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Adjustments */}
              {showAdjustments && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Adjustments</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Brightness</label>
                      <input
                        type="range"
                        min="50"
                        max="150"
                        value={filters.brightness}
                        onChange={e =>
                          setFilters(prev => ({ ...prev, brightness: Number(e.target.value) }))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">{filters.brightness}%</span>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Contrast</label>
                      <input
                        type="range"
                        min="50"
                        max="150"
                        value={filters.contrast}
                        onChange={e =>
                          setFilters(prev => ({ ...prev, contrast: Number(e.target.value) }))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">{filters.contrast}%</span>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Saturation</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={filters.saturation}
                        onChange={e =>
                          setFilters(prev => ({ ...prev, saturation: Number(e.target.value) }))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">{filters.saturation}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
